import { memo } from "react";
import { Bot, Loader2, ShoppingCart, DollarSign, Pause, Layers, Sparkles, AlertTriangle } from "lucide-react";

/**
 * Non-blocking pill banner that appears at the top of the play area to show
 * the AI's intention. Two states:
 *   • thinking — shows "Bot X is thinking…" with a spinner
 *   • action   — shows "Bot X chose: BUY BTC" with a verb-tinted icon
 *
 * Props:
 *   thinking: string | null      — bot name currently thinking, or null
 *   action:   {botName, verb, symbol?, cardName?, type} | null
 *
 * Wrapped in `memo` so a `game` state update that doesn't change the
 * staging banner skips this re-render entirely (KNOWN_ISSUES §1.36).
 */
function AIActionIndicator({ thinking, action }) {
  if (!thinking && !action) return null;

  if (thinking) {
    return (
      <div
        className="absolute left-1/2 -translate-x-1/2 top-2 z-30 pointer-events-none"
        data-testid="ai-thinking-banner"
      >
        <div className="flex items-center gap-2 rounded-full bg-[#1A237E]/90 dark:bg-[#E3F2FD]/90 backdrop-blur text-white px-4 py-2 shadow-lg border border-white/20 animate-bounce-in">
          <Loader2 className="w-4 h-4 animate-spin text-[#8247E5]" />
          <span className="text-sm font-bold" style={{ fontFamily: "Fredoka" }}>
            {thinking} is thinking…
          </span>
        </div>
      </div>
    );
  }

  const { botName, verb, symbol, cardName, type } = action;
  const visual = verbVisual(type || verb);

  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 top-2 z-30 pointer-events-none"
      data-testid="ai-action-banner"
    >
      <div
        className={`flex items-center gap-2 rounded-full ${visual.bg} ${visual.text} px-4 py-2 shadow-lg border border-white/20 animate-bounce-in`}
        data-testid={`ai-action-${(type || verb).toLowerCase()}`}
      >
        <Bot className="w-4 h-4 opacity-90" />
        <span className="text-xs font-semibold opacity-80" style={{ fontFamily: "Fredoka" }}>
          {botName} chose:
        </span>
        <visual.Icon className="w-4 h-4" />
        <span className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: "Fredoka" }}>
          {verb}
          {symbol ? ` ${symbol}` : ""}
          {cardName ? ` ${cardName}` : ""}
        </span>
      </div>
    </div>
  );
}

export default memo(AIActionIndicator);


function verbVisual(type) {
  switch (type) {
    case "buy":
      return { bg: "bg-[#00E676]", text: "text-white", Icon: ShoppingCart };
    case "buy_fail":
      return { bg: "bg-[#FF5A5F]", text: "text-white", Icon: AlertTriangle };
    case "sell":
      return { bg: "bg-[#FF5A5F]", text: "text-white", Icon: DollarSign };
    case "hold":
      return { bg: "bg-[#FFC107]", text: "text-[#1A237E] dark:text-[#E3F2FD]", Icon: Pause };
    case "draw":
      return { bg: "bg-[#38B6FF]", text: "text-white", Icon: Layers };
    case "play":
      return { bg: "bg-[#8247E5]", text: "text-white", Icon: Sparkles };
    default:
      return { bg: "bg-[#1A237E]", text: "text-white", Icon: Bot };
  }
}
